import { calculateMonthlyContributionWithFee } from "Charts/Utils/CommonChartUtils";
import { calculateMonthlyContribution, calculateMonthlyPayment } from "Charts/Utils/PropertyPurchaseChartUtils";
import type { HousingPropertyGoalFormType } from "Forms/Schemas/Goals/housingPropertyFormSchema";
import { differenceInMonths } from "date-fns";
// import { differenceInCalendarMonths } from "date-fns";
import type { UseFormSetValue, UseFormWatch } from "react-hook-form";

type Props = {
  watch: UseFormWatch<HousingPropertyGoalFormType>;
  setValue: UseFormSetValue<HousingPropertyGoalFormType>;
};

const parseField = (value: any) => {
  if (value === "" || value === null || value === undefined) return Number.NaN;
  return Number(value);
};

export const useHousingPropertyCalculations = ({ watch, setValue }: Props) => {
  const loanAmount = parseField(watch("loanAmount") || 0);
  const loanDuration = parseField(watch("loanDuration") || 0);
  const currentSavingsAmount = parseField(watch("currentSavingsAmount"));
  const downpaymentAmount = parseField(watch("downpaymentAmount"));
  const expectedPriceAmount = parseField(watch("expectedPriceAmount"));
  const targetDate = watch("targetDate");
  const referenceStartDate = watch("referenceStartDate") || new Date();
  const loanInterestRate = parseField(watch("loanInterestRate"));
  const includeInflation = watch("includeInflation");
  const inflationRate = parseField(watch("inflationRate"));
  const interestRate = parseField(watch("interestRate"));
  const feeRate = parseField(watch("feeRate"));
  const investCurrentSavings = watch("investCurrentSavings");
  const loanToValue = parseField(watch("loanToValue"));
  const includeEntryFee = watch("includeEntryFee");

  const loanPaymentAmount =
    loanAmount > 0 && loanDuration > 0 && loanInterestRate >= 0
      ? calculateMonthlyPayment({
          loanInterestRate,
          loanAmount,
          durationInYears: loanDuration,
        })
      : 0;

  const toSaveUp = Math.max(0, downpaymentAmount - (currentSavingsAmount || 0));
  const durationInMonths = targetDate ? Math.max(0, differenceInMonths(targetDate, referenceStartDate)) : 0;
  const hasEnoughMoney = expectedPriceAmount > 0 && currentSavingsAmount >= expectedPriceAmount;
  const loanTotalAmount = loanPaymentAmount * (loanDuration * 12);
  const interestPaid = loanTotalAmount > 0 && loanAmount > 0 ? loanTotalAmount - loanAmount : 0;
  const overpayment = loanTotalAmount > 0 ? (interestPaid / loanTotalAmount) * 100 : 0;

  const monthlyContribution =
    currentSavingsAmount >= 0 && toSaveUp > 0 && durationInMonths > 0 && interestRate >= 0
      ? calculateMonthlyContribution({
          startAmount: currentSavingsAmount,
          targetAmount: downpaymentAmount,
          inflationRate: includeInflation && inflationRate >= 0 ? inflationRate : 0,
          interestRate,
          durationInMonths,
          investCurrentSavings,
        })
      : 0;

  const monthlyContributionWithFee =
    monthlyContribution > 0
      ? includeEntryFee && feeRate > 0
        ? calculateMonthlyContributionWithFee(monthlyContribution, feeRate)
        : monthlyContribution
      : 0;

  const handleExpectedPriceChange = (value: string) => {
    const newPrice = parseField(value);
    if (newPrice > 0 && loanToValue >= 0) {
      const newLoanAmount = newPrice * (loanToValue / 100);
      const newDownpayment = newPrice - newLoanAmount;

      setValue("loanAmount", newLoanAmount);
      setValue("downpaymentAmount", newDownpayment);
    }
  };

  const handleLtvChange = (value: string) => {
    const newLtv = parseField(value);
    if (expectedPriceAmount > 0 && newLtv >= 0) {
      const newLoanAmount = expectedPriceAmount * (newLtv / 100);
      const newDownpayment = expectedPriceAmount - newLoanAmount;

      setValue("loanAmount", newLoanAmount);
      setValue("downpaymentAmount", newDownpayment);
    }
  };

  const handleDownpaymentChange = (value: string) => {
    const newDownpayment = parseField(value);
    if (expectedPriceAmount > 0 && newDownpayment >= 0) {
      const newLoanAmount = expectedPriceAmount - newDownpayment;
      const newLtv = (newLoanAmount / expectedPriceAmount) * 100;

      setValue("loanAmount", newLoanAmount);
      setValue("loanToValue", newLtv);
    }
  };

  const handleLoanAmountChange = (value: string) => {
    const newLoanAmount = parseField(value);
    if (expectedPriceAmount > 0 && newLoanAmount >= 0) {
      const newLtv = (newLoanAmount / expectedPriceAmount) * 100;
      const newDownpayment = expectedPriceAmount - newLoanAmount;

      setValue("loanToValue", newLtv);
      setValue("downpaymentAmount", newDownpayment);
    }
  };

  return {
    calculations: {
      loanTotalAmount,
      interestPaid,
      overpayment,
      toSaveUp,
      hasEnoughMoney,
      durationInMonths,
      loanPaymentAmount,
      monthlyContribution,
      monthlyContributionWithFee,
    },
    handlers: { handleExpectedPriceChange, handleDownpaymentChange, handleLoanAmountChange, handleLtvChange },
  };
};
